import React, { useRef, useEffect, useCallback, useState } from "react";
import * as f3 from "family-chart";
import "family-chart/styles/family-chart.css";
import "./FamilyConnect.css";
import { SharedButton, SharedFeatureError } from "../../shared";
import CousinLookupLoading from "./CousinLookupLoading";
import { useGetFamilyTreeQuery } from "../../../store/slices/api";
import PersonPicker from "./PersonPicker";
import RelationshipResult from "./RelationshipResult";
import FilterPanel from "./FilterPanel";
import { TREE_ROOT_ID } from "../FamilyTree/treeRoot";

function ConnectionPathFinder() {
  // Refs for the hidden chart (used only for kinship calculations)
  const hiddenChartRef = useRef(null);
  const f3ChartRef = useRef(null);

  // Refs for the visible mini chart that shows the connection path
  const miniChartContainerRef = useRef(null);
  const miniChartInstanceRef = useRef(null);

  // UI state
  const [personA, setPersonA] = useState(null);
  const [personB, setPersonB] = useState(null);
  const [relationship, setRelationship] = useState(null);
  const [error, setError] = useState(null);
  const [isSearching, setIsSearching] = useState(false);
  // The people on the connection path, once found. The mini chart is drawn
  // from these after its box is on screen (see the effect below).
  const [miniChartData, setMiniChartData] = useState(null);
  const showMiniChart = miniChartData !== null;
  const [miniChartFailed, setMiniChartFailed] = useState(false);
  const [selectedGenerations, setSelectedGenerations] = useState([]);
  const [selectedEras, setSelectedEras] = useState([]);

  // Load the family tree data (same data the /tree page uses, cached by RTK Query)
  const { data: familyTree, isLoading, isFetching, isError, refetch } = useGetFamilyTreeQuery();

  // Initialize a hidden chart instance for kinship calculations.
  // This chart is never displayed to the user — it just gives us access
  // to the library's calculateKinships and getKinshipsDataStash methods.
  //
  // A charting failure stays in this panel (Find Connection then says
  // something went wrong). Thrown from here it would reach the app-wide error
  // screen, since this page lives inside the always-mounted layout.
  useEffect(() => {
    if (hiddenChartRef.current && familyTree && !isLoading) {
      try {
        const dataCopy = structuredClone(familyTree);

        // The library expects a "gender" field for gender-specific labels.
        // Our API sends "sex" with the same values, so we copy it over.
        dataCopy.forEach((d) => {
          if (d.data.sex && !d.data.gender) {
            d.data.gender = d.data.sex;
          }
        });

        const chart = f3.createChart("#HiddenChart", dataCopy);

        chart
          .setCardHtml()
          .setCardDisplay([
            ["first_name", "last_name", "numeric_suffix"],
            ["birthday"],
          ]);

        chart.updateMainId(TREE_ROOT_ID); // see treeRoot.js
        chart.updateTree({ initial: true });
        f3ChartRef.current = chart;
      } catch (err) {
        console.warn("Error preparing the kinship chart:", err);
        f3ChartRef.current = null;
      }
    }
  }, [familyTree, isLoading]);

  // Clean up the mini chart when we run a new search
  const clearMiniChart = useCallback(() => {
    if (miniChartContainerRef.current) {
      miniChartContainerRef.current.innerHTML = "";
    }
    miniChartInstanceRef.current = null;
    setMiniChartData(null);
    setMiniChartFailed(false);
  }, []);

  // Build a small focused chart showing only the people in the connection chain.
  //
  // This used to draw straight into the chart's box, but the box was only put
  // on the page once the chart existed, so it was never there to draw into and
  // the chart never appeared. Now the box is always on the page (hidden while
  // empty): the path is kept here, and the effect below draws it once the box
  // is showing and has a size to fit the chart to.
  const buildMiniChart = useCallback(
    (stashData) => {
      clearMiniChart();

      if (!stashData || stashData.length === 0) return;

      // Add gender field to the stash data too
      stashData.forEach((d) => {
        if (d.data.sex && !d.data.gender) {
          d.data.gender = d.data.sex;
        }
      });

      setMiniChartData(stashData);
    },
    [clearMiniChart]
  );

  useEffect(() => {
    if (!miniChartData || !miniChartContainerRef.current) return;
    miniChartContainerRef.current.innerHTML = "";

    // If the chart can't be drawn, the relationship found stays on screen and
    // the chart's box says so, instead of the whole app showing its error page.
    try {
      const miniChart = f3.createChart("#MiniChart", miniChartData);
      miniChartInstanceRef.current = miniChart;

      miniChart
        .setCardHtml()
        .setCardDisplay([
          ["first_name", "last_name", "numeric_suffix"],
          ["birthday"],
        ]);

      miniChart.updateTree({ initial: true, tree_position: "fit" });
    } catch (err) {
      console.warn("Error drawing the connection chart:", err);
      if (miniChartContainerRef.current) miniChartContainerRef.current.innerHTML = "";
      miniChartInstanceRef.current = null;
      setMiniChartFailed(true);
    }
  }, [miniChartData]);

  const handleFindConnection = useCallback(() => {
    const chart = f3ChartRef.current;
    if (!chart) {
      // The kinship chart couldn't be prepared (see above).
      setError("Something went wrong while calculating the connection.");
      return;
    }

    setError(null);
    setRelationship(null);
    clearMiniChart();

    // Validate selections
    if (!personA || !personB) {
      setError("Please select both Person A and Person B.");
      return;
    }

    if (personA.id === personB.id) {
      setRelationship("self");
      return;
    }

    setIsSearching(true);

    // Use setTimeout to let the UI update before running the calculation
    setTimeout(() => {
      try {
        // Calculate kinships from Person B's perspective, then look up A.
        // This gives us "what A is to B" — e.g. if A=Sophie, B=Sam,
        // the label is "Aunt" (Sophie is the aunt of Sam).
        const kinships = chart.calculateKinships(personB.id, {
          show_in_law: true,
        });

        const label = kinships[personA.id];

        if (!label) {
          setError(
            "No common ancestor was found between these two people. They may be in separate branches that don't share a recorded link.\n\nIf you believe they should be connected, the records in our database may be incomplete."
          );
          setIsSearching(false);
          return;
        }

        setRelationship(label);

        // Get the minimal data set showing just the connection path
        const stashData = chart.getKinshipsDataStash(personA.id, personB.id);

        if (stashData && stashData.length > 0) {
          buildMiniChart(stashData);
        }
      } catch (err) {
        console.warn("Error calculating kinship:", err);
        setError("Something went wrong while calculating the connection.");
      }

      setIsSearching(false);
    }, 50);
  }, [personA, personB, clearMiniChart, buildMiniChart]);

  // Also while "Try again" runs: RTK Query then reports neither loading nor
  // error and no data, which would read as "No family tree data found."
  if (isLoading || (!familyTree && isFetching)) {
    return <CousinLookupLoading mode="initial" />;
  }

  // A failed load is not an empty tree: say so, and offer to try again.
  if (isError && !familyTree) {
    return <SharedFeatureError onRetry={refetch} />;
  }

  if (!familyTree) {
    return <div>No family tree data found.</div>;
  }

  const canSearch = personA && personB && !isSearching;

  return (
    <div className="flex flex-col w-full gap-y-[24px]">
      {/* Filters and person pickers */}
      <div className="flex flex-col gap-y-[16px]">
        <FilterPanel
          familyTreeData={familyTree}
          selectedGenerations={selectedGenerations}
          onGenerationsChange={setSelectedGenerations}
          selectedEras={selectedEras}
          onErasChange={setSelectedEras}
        />
        <div className="flex flex-row gap-x-[16px] flex-wrap gap-y-[12px] items-center">
          <PersonPicker
            familyTreeData={familyTree}
            onSelect={setPersonA}
            value={personA}
            label="Person A"
            filters={{ generations: selectedGenerations, eras: selectedEras }}
          />
          <button
            className="swap-button"
            title="Swap Person A and Person B"
            disabled={!personA && !personB}
            onClick={() => {
              setPersonA(personB);
              setPersonB(personA);
              setRelationship(null);
              setError(null);
              clearMiniChart();
            }}
          >
            <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M14 4L17 7L14 10" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>
              <path d="M3 7H17" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round"/>
              <path d="M6 16L3 13L6 10" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>
              <path d="M17 13H3" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round"/>
            </svg>
          </button>
          <PersonPicker
            familyTreeData={familyTree}
            onSelect={setPersonB}
            value={personB}
            label="Person B"
            filters={{ generations: selectedGenerations, eras: selectedEras }}
          />
        </div>
        <div className="flex flex-col items-center gap-y-[4px]">
          <SharedButton
            className="inline-block rounded-full px-[24px] py-[10px] w-fit text-center"
            disabled={!canSearch}
            onClick={handleFindConnection}
          >
            Find Connection
          </SharedButton>
          {isSearching && <CousinLookupLoading mode="searching" />}
        </div>
      </div>

      {/* Relationship result card */}
      <RelationshipResult
        personA={personA}
        personB={personB}
        relationship={relationship}
        error={error}
      />

      {/* Hidden chart — used only for kinship calculations, never shown */}
      <div
        id="HiddenChart"
        ref={hiddenChartRef}
        className="hidden-chart"
      />

      {/* Mini connection chart — shows the connection path. Always on the
          page so the chart has somewhere to draw; hidden until there is one. */}
      <div className={showMiniChart ? "flex flex-col gap-y-[8px]" : "hidden"}>
        <p className="text-dupontGray dark:text-darkTextSecondary text-[14px] text-center">
          Connection Path
        </p>
        {miniChartFailed && (
          <p className="text-dupontGray dark:text-darkTextSecondary text-[14px] text-center italic">
            The chart of this connection couldn’t be drawn this time.
          </p>
        )}
        <div
          className={`f3 mini-chart-container${miniChartFailed ? " hidden" : ""}`}
          id="MiniChart"
          ref={miniChartContainerRef}
        />
      </div>
    </div>
  );
}

export default ConnectionPathFinder;
